/**
 * PersonIdentity
 *
 * Who a row is about: handle over display name.
 *
 * The handle leads because it is the key the user actually works with. Both
 * domains that use this match people by **exact username** and offer no
 * search — `POST /api/friend` and `POST /api/trainer` alike — so the handle is
 * what someone types to reach a person and what they scan for to check
 * whether they already have. A row showing only "Nia Adeyemi" cannot answer
 * "have I already asked @nia_a?", which is the sent panel's entire reason to
 * exist.
 *
 * It also leads because it is the only line guaranteed to be there.
 * `users.name` is optional at registration and independently nullable, so a
 * title built from it changes shape with the data — this component used to
 * swap its title's FONT depending on whether a name had been released, which
 * meant the standing board rendered one structure on the friends scope and a
 * different one on the global scope, where the API nulls every name. The
 * title is now invariant: always the handle, always mono.
 *
 * The display name is opt-in via `showName` and permitted only where the
 * relationship releasing it exists. A pending request is a stranger asking;
 * their real name is not something the user agreed to be told. `showName`
 * governs whether the name is printed at all — never where it sits.
 *
 * Lives in `ui/` because six surfaces render this across Friends, Trainers
 * and Standing, and the null-name rule must not drift between them.
 *
 * Built from block `span`s rather than `div`/`p`, so it can sit inside a
 * button (the clients ledger's rows are one), which only takes phrasing
 * content.
 */

import type { PersonIdentityProps } from './PersonIdentity.types';

export function PersonIdentity({
  person,
  meta,
  compact = false,
  showName = false,
  className = '',
}: PersonIdentityProps): React.JSX.Element {
  // Trimmed, not just truthy: `users.name` is free text, and a whitespace-only
  // value would otherwise render a blank title above the handle.
  const name = person.name?.trim() ?? '';
  const hasName = showName && name.length > 0;
  const handle = `@${person.username}`;

  return (
    <span className={`block min-w-0 flex-1 ${className}`}>
      {/* Not `Text`: it always sets `font-sans`, which beats a `font-mono`
          passed in by stylesheet order, and the handle is always mono. */}
      <span
        data-testid="person-identity-handle"
        className={`block truncate font-mono text-neutral-50 ${
          compact ? 'text-body-sm' : 'text-body-md'
        }`}
      >
        {handle}
      </span>

      <span
        data-testid="person-identity-detail"
        className="mt-0.5 flex items-center gap-1.5 truncate text-body-sm text-neutral-500"
      >
        {hasName ? <span className="truncate">{name}</span> : null}
        {hasName && meta ? <span aria-hidden="true">·</span> : null}
        {meta ? <span className="truncate">{meta}</span> : null}
      </span>
    </span>
  );
}

export default PersonIdentity;
